// src/components/team/VisibilityAssignmentModal.tsx
// Modal for assigning records to a member's selected visibility
//
// Allows searching and selecting records to add to a member's visibility.

import { useState, useMemo } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useGetProjectsQuery } from '../../api/projectsApi';
import type { EntityConfig } from '../../api/memberVisibilityApi';

// ============================================================
// Types
// ============================================================

interface VisibilityAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (ids: string[]) => Promise<void>;
  config: EntityConfig;
  workspaceId: string;
  existingIds: string[];
}

interface SelectableRecord {
  id: string;
  name: string;
}

// ============================================================
// Component
// ============================================================

export default function VisibilityAssignmentModal({
  isOpen,
  onClose,
  onAssign,
  config,
  workspaceId,
  existingIds,
}: VisibilityAssignmentModalProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  // Fetch available records based on entity type
  const { data: projects, isLoading: isLoadingProjects } = useGetProjectsQuery(
    { workspaceId, q: searchQuery || undefined },
    { skip: !isOpen || config.entity !== 'projects' }
  );

  // For calendars, we'll use a placeholder until the calendar API exists
  // In a real implementation, this would fetch from scheduler.calendars endpoint

  // Build available records list
  const availableRecords: SelectableRecord[] = useMemo(() => {
    if (config.entity === 'projects' && projects) {
      return projects
        .filter((p) => !existingIds.includes(p.id))
        .map((p) => ({ id: p.id, name: p.name }));
    }

    // Placeholder for calendars until API exists
    if (config.entity === 'calendars') {
      // In production, this would call the scheduler calendars API
      return [];
    }

    return [];
  }, [config.entity, projects, existingIds]);

  const isLoading = isLoadingProjects;

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === availableRecords.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(availableRecords.map((r) => r.id)));
    }
  };

  const handleAssign = async () => {
    if (selectedIds.size === 0) return;

    setIsAssigning(true);
    try {
      await onAssign(Array.from(selectedIds));
      setSelectedIds(new Set());
      setSearchQuery('');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleClose = () => {
    setSelectedIds(new Set());
    setSearchQuery('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Add ${config.pluralLabel}`}
      description={`Select ${config.pluralLabel.toLowerCase()} to share with this member`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isAssigning}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAssign}
            disabled={selectedIds.size === 0}
            isLoading={isAssigning}
          >
            Add {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Search Input */}
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            placeholder={`Search ${config.pluralLabel.toLowerCase()}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Select All */}
        {availableRecords.length > 0 && (
          <div className="flex items-center justify-between px-1">
            <span className="text-sm text-gray-500">
              {availableRecords.length} available
            </span>
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              {selectedIds.size === availableRecords.length ? 'Deselect all' : 'Select all'}
            </button>
          </div>
        )}

        {/* Records List */}
        <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
          {isLoading ? (
            <div className="p-4 text-center text-gray-500">
              <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full mx-auto" />
              <p className="mt-2 text-sm">Loading...</p>
            </div>
          ) : availableRecords.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              <p className="text-sm">
                {searchQuery
                  ? `No ${config.pluralLabel.toLowerCase()} found matching "${searchQuery}"`
                  : `No ${config.pluralLabel.toLowerCase()} available to assign`}
              </p>
            </div>
          ) : (
            availableRecords.map((record) => (
              <label
                key={record.id}
                className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(record.id)}
                  onChange={() => handleToggleSelect(record.id)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="flex-1 text-sm text-gray-900 truncate">
                  {record.name}
                </span>
              </label>
            ))
          )}
        </div>

        {/* Existing assignments note */}
        {existingIds.length > 0 && (
          <p className="text-xs text-gray-500">
            {existingIds.length} {config.pluralLabel.toLowerCase()} already assigned
          </p>
        )}
      </div>
    </Modal>
  );
}
